/* eslint-disable react-compiler/react-compiler */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  Box,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useApi } from "@platform/api";
import { Alert, Button, Card, Link, Typography } from "@platform/design-system";

import { useAuth, useValidateRules } from "../../hooks";
import SuccessMessage from "../../components/successMessage";
import {
  STATE_RULES,
  initPasswordRulesConfirm,
  ROUTE_NAMES,
} from "../../constants";
import { RenderRules, Loader } from "../../components";
import { isValidEmail, extractErrorMessage } from "../../utils";
import { submitEvent } from "../../utils/tracking";
import { clearUser } from "../../utils/tracking";
import { getProjectEnvVariables } from "../../projectEnvVariables";

const { envVariables } = getProjectEnvVariables();

const baseURL = "brokerportal/v1";
const FIRST_NAME = "firstName";
const LAST_NAME = "lastName";
const EMAIL = "email";
const PHONE = "phone";
const PASSWORD = "password";
const CONFIRM_PASSWORD = "confirmPassword";

const initUser = {
  [FIRST_NAME]: "",
  [LAST_NAME]: "",
  [EMAIL]: "",
  [PHONE]: "",
  [PASSWORD]: "",
  [CONFIRM_PASSWORD]: "",
};
const initLoading = {
  show: false,
  msg: "",
};

const Onboarding = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(true);
  const [loading, setLoading] = useState(() =>
    token ? { ...initLoading, show: true } : initLoading,
  );
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [user, setUser] = useState(initUser);
  const [success, setSuccess] = useState(false);
  const [rulesOther, setRulesOther] = useState(initPasswordRulesConfirm);
  const [isPswFocus, setIsPswFocus] = useState(0);
  const theme = useTheme();
  const axiosInstance = useApi();
  const navigate = useNavigate();
  const [validateRule, rules, restoreRules] = useValidateRules();
  const { logOut, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      logOut();
      clearUser();
    }
  }, []);

  useEffect(() => {
    const prepareOnboarding = async () => {
      try {
        setLoading({ show: true, msg: "Validating token" });
        const { data } = await axiosInstance.post(
          `${baseURL}/prepareOnboarding`,
          {
            token,
          },
        );
        setUser({ ...user, ...data });
        setLoading(initLoading);
        setIsTokenValid(true);
      } catch (error) {
        submitEvent("Invalid Invitation Seen");
        setLoading(initLoading);
        if (error?.response?.data?.errorMessage == "Token cancelled") {
          navigate(`/${ROUTE_NAMES.ONBOARDING_ERROR}?caseId=cancelled`);
        } else if (
          error?.response?.data?.errorMessage == "Token already used"
        ) {
          navigate(`/${ROUTE_NAMES.ONBOARDING_ERROR}?caseId=exist`);
        } else {
          navigate(`/${ROUTE_NAMES.ONBOARDING_ERROR}?caseId=expired`);
        }
        setIsTokenValid(false);
      }
    };

    if (token) {
      prepareOnboarding();
    }
  }, [token, axiosInstance]);

  const validateRuleOther = (val) => {
    const newRules = [...rulesOther];

    if (user.password === val)
      newRules[0] = { ...newRules[0], state: STATE_RULES.DONE };
    else newRules[0] = { ...newRules[0], state: STATE_RULES.WRONG };

    setRulesOther(newRules);
  };

  const handleChange = ({ target }) => {
    const { name, value } = target;
    setUser({ ...user, [name]: value });
    if (name === PASSWORD) validateRule(value);
    else if (name === CONFIRM_PASSWORD) validateRuleOther(value);
    else if (value) setValidationErrors({ ...validationErrors, [name]: "" });
  };

  const handleBlur = ({ target }) => {
    const { name, value } = target;
    if (name === PASSWORD) validateRule(value);
    else if (name === CONFIRM_PASSWORD) validateRuleOther(value);
    else if (name === EMAIL && !!value && !isValidEmail(value))
      setValidationErrors({ ...validationErrors, [name]: "Invalid email" });
    else if (!value)
      setValidationErrors({ ...validationErrors, [name]: "No empty value" });
  };

  const isValidInfo = () =>
    Object.values(user).every(Boolean) &&
    user[PASSWORD] === user[CONFIRM_PASSWORD] &&
    rules.every((rule) => rule.state === STATE_RULES.DONE) &&
    !Object.values(validationErrors).every(Boolean);

  const addUser = async () => {
    setLoading({ show: true, msg: "Adding user" });
    if (isValidInfo()) {
      const objRequest = {
        token,
        firstName: user[FIRST_NAME],
        phone: user[PHONE],
        lastName: user[LAST_NAME],
        password: user[PASSWORD],
      };
      try {
        await axiosInstance.post(`${baseURL}/createAccount`, objRequest);
        setLoading(initLoading);
        setSuccess(true);
      } catch (err) {
        setLoading(initLoading);
        if (err?.response?.data?.errorMessage == "Token cancelled") {
          navigate(`/${ROUTE_NAMES.ONBOARDING_ERROR}?caseId=cancelled`);
        } else if (err?.response?.data?.errorMessage == "Token already used") {
          navigate(`/${ROUTE_NAMES.ONBOARDING_ERROR}?caseId=exist`);
        }
        setError(extractErrorMessage(err));
      }
    } else {
      setLoading(initLoading);
      setError("Complete/Check your information");
    }
  };

  const handleSubmit = async (e) => {
    if (loading.show) return;
    if (e.key === "Enter") {
      addUser();
    }
  };

  const handleCancel = async () => {
    setUser({ ...user, password: "", confirmPassword: "", phone: "" });
    restoreRules();
  };

  const logInRedirect = () => {
    navigate("/");
  };

  if (!token)
    return (
      <Grid container mt={2} justifyContent="center" alignItems="center">
        <Typography variant="display-medium">Token is needed</Typography>
      </Grid>
    );

  if (!isTokenValid)
    return (
      <Grid container mt={2} justifyContent="center" alignItems="center">
        <Typography variant="display-medium">Token is not valid</Typography>
      </Grid>
    );

  return loading.show ? (
    <Loader />
  ) : success ? (
    <SuccessMessage
      title={`Congratulations ${user.firstName}!`}
      subtitle="Your account has been successfully created."
      buttonText="Continue"
      buttonAction={logInRedirect}
    />
  ) : (
    <Box maxWidth={theme.breakpoints.values.sm}>
      <Typography variant="display-medium" marginBottom={5}>
        Create your account
      </Typography>
      <Card variant="elevated" padding="none" sx={{ px: "3.25rem" }}>
        <Typography variant="header-large" mt={3}>
          Please provide the following information
        </Typography>
        {error && (
          <Box marginY={2}>
            <Alert severity="error" variant="filled">
              {error}
            </Alert>
          </Box>
        )}
        <Grid container mt={1} spacing={2}>
          {/* TODO(ds-adopt): TextField — no base component yet (applies to the fields below) */}
          <Grid size={12}>
            <TextField
              fullWidth
              value={user[FIRST_NAME]}
              onChange={handleChange}
              onKeyPress={handleSubmit}
              onBlur={handleBlur}
              name={FIRST_NAME}
              error={!!validationErrors[FIRST_NAME]}
              helperText={validationErrors[FIRST_NAME]}
              label="First Name"
              required
              autoComplete="none"
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              value={user[LAST_NAME]}
              onChange={handleChange}
              onKeyPress={handleSubmit}
              onBlur={handleBlur}
              name={LAST_NAME}
              error={!!validationErrors[LAST_NAME]}
              helperText={validationErrors[LAST_NAME]}
              label="Last Name"
              required
              autoComplete="none"
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              value={user[PHONE]}
              onChange={handleChange}
              onKeyPress={handleSubmit}
              onBlur={handleBlur}
              name={PHONE}
              error={!!validationErrors[PHONE]}
              helperText={validationErrors[PHONE]}
              label="Phone Number"
              required
              autoComplete="none"
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              value={user[EMAIL]}
              onChange={handleChange}
              onKeyPress={handleSubmit}
              onBlur={handleBlur}
              name={EMAIL}
              error={!!validationErrors[EMAIL]}
              helperText={validationErrors[EMAIL]}
              label="Email Address"
              required
              autoComplete="none"
            />
          </Grid>
          <Grid size={12}>
            <TextField
              value={user[PASSWORD]}
              fullWidth
              name={PASSWORD}
              type={showPassword ? "text" : "password"}
              onChange={handleChange}
              onKeyPress={handleSubmit}
              onFocus={() => setIsPswFocus(1)}
              onBlur={() => setIsPswFocus(0)}
              label="New Password"
              required
              autoComplete="new-password"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          {isPswFocus === 1 && (
            <Grid size={12}>
              <RenderRules rules={rules} />
            </Grid>
          )}
          <Grid size={12}>
            <TextField
              value={user[CONFIRM_PASSWORD]}
              fullWidth
              type={showConfirmPassword ? "text" : "password"}
              onChange={handleChange}
              onKeyPress={handleSubmit}
              onFocus={() => setIsPswFocus(2)}
              onBlur={() => setIsPswFocus(0)}
              label="Confirm Password"
              name={CONFIRM_PASSWORD}
              required
              autoComplete="new-password"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          {isPswFocus === 2 && (
            <Grid size={12}>
              <RenderRules rules={rulesOther} />
            </Grid>
          )}
        </Grid>
        <Box mt={3} mb={3}>
          <Button
            variant="primary"
            type="submit"
            disabled={!isValidInfo()}
            onClick={() => addUser()}
          >
            Confirm
          </Button>
          <Button
            variant="tertiary"
            type="button"
            onClick={() => handleCancel()}
          >
            Undo Changes
          </Button>
        </Box>
        <Box mb={3}>
          By creating an account, you agree to the{" "}
          <Link
            href={envVariables.VITE_REACT_APP_TERM_OF_USE_URL || "/"}
            target="_blank"
            rel="noreferrer"
            underline="none"
            sx={{ fontWeight: "bold" }}
          >
            Terms and Conditions
          </Link>
          .
        </Box>
      </Card>
    </Box>
  );
};

export default Onboarding;
